import { Card, Spinner } from "react-bootstrap";
import { FileText, TrendingUp, ArrowRight, MousePointerClick } from "lucide-react";

import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { api } from "helpers/api_url";
import { WarehouseOption } from "./DashboardFilters";
import { SortingState, ColumnFiltersState } from "@tanstack/react-table";
import { CustomColumnDef } from "Common/Tables/ObjectFiltersTable/DashboardTableContainer";
import { fetchQuotationAnalytics, fetchQuotationList } from "slices/Dashboard/Quotation_Summary/thunk";
import { DashboardSlidePanel } from "./Reusable/DashboardSlidePanel";
import "./QuotationSummary.css";

interface QuotationSummaryProps {
  dateFrom?: Date;
  dateTo?: Date;
  warehouse?: WarehouseOption[];
}


interface QuotationListingItem {
  quotation_id: string;
  quotation_number: string;
  quotation_date: string;
  customer_name: string;
  contact_person_name: string;
  contact_person_phone_no: string;
  total_amount: string;
  stage: string;
  source: 'from_enquiry' | 'direct';
  enquiry_id: string | null;
  enquiry_number: string | null;
  created_at: string;
  updated_at: string;
  created_by_username: string | null;
  updated_by_username: string | null;
}

export function QuotationSummary({ dateFrom, dateTo, warehouse = [] }: QuotationSummaryProps) {
  const dispatch = useDispatch<any>();
  const { analytics: quotationAnalyticsData, analyticsLoading: loading, quotationList, totalRecords, listLoading } = useSelector((state: any) => state.QuotationSummaryReducer);
  
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'fromEnquiry' | 'direct'>('all');
  
  // Table state
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [filterValue, setFilterValue] = useState<any[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);

  // Fetch quotation analytics data
  useEffect(() => {
    dispatch(fetchQuotationAnalytics({ dateFrom, dateTo, warehouse })).catch((error: any) => {
      console.error("Error fetching quotation analytics data:", error);
      toast.error("Failed to load quotation analytics data");
    });
  }, [dispatch, dateFrom, dateTo, warehouse]);

  const totalQuotations = quotationAnalyticsData?.total_quotations ?? 0;
  const fromEnquiry = quotationAnalyticsData?.from_enquiry ?? 0;
  const direct = quotationAnalyticsData?.direct ?? 0;
  const won = quotationAnalyticsData?.won ?? 0;
  const lost = quotationAnalyticsData?.lost ?? 0;
  const pending = quotationAnalyticsData?.pending ?? 0;
  const fromEnquiryWon = quotationAnalyticsData?.from_enquiry_won ?? 0;
  const fromEnquiryLost = quotationAnalyticsData?.from_enquiry_lost ?? 0;
  const fromEnquiryPending = quotationAnalyticsData?.from_enquiry_pending ?? 0;
  const directWon = quotationAnalyticsData?.direct_won ?? 0;
  const directLost = quotationAnalyticsData?.direct_lost ?? 0;
  const directPending = quotationAnalyticsData?.direct_pending ?? 0;

  const conversionRate = totalQuotations > 0 ? Math.round((won / totalQuotations) * 100) : 0;

  const handleCardClick = (filter: 'all' | 'fromEnquiry' | 'direct') => {
    setSelectedFilter(filter);
    setIsPanelOpen(true);
    setPageIndex(0); // Reset to first page when opening panel
  };

  // Convert selectedFilter to API filter_type
  const filterType = useMemo((): 'all' | 'from_enquiry' | 'direct' => {
    if (selectedFilter === 'fromEnquiry') return 'from_enquiry';
    if (selectedFilter === 'direct') return 'direct';
    return 'all';
  }, [selectedFilter]);


  // Fetch quotation list data
  useEffect(() => {
    if (!isPanelOpen) return;

    dispatch(fetchQuotationList({
      page: pageIndex + 1,
      pageSize,
      filterType,
      dateFrom,
      dateTo,
      warehouse,
      sorting,
      filters: filterValue,
    })).catch((error: any) => {
      console.error("Error fetching quotation list:", error);
      toast.error("Failed to load quotation list");
    });
  }, [dispatch, isPanelOpen, pageIndex, pageSize, sorting, filterValue, filterType, dateFrom, dateTo, warehouse]);

  // Reset page when filters change
  useEffect(() => {
    if (isPanelOpen) {
      setPageIndex(0);
    }
  }, [sorting, pageSize, columnFilters, selectedFilter, dateFrom, dateTo, warehouse, isPanelOpen]);

  const getQuotationStatusBadge = (status: string) => {
    const statusConfig: Record<string, { className: string; label: string }> = {
      'won': { className: 'quotation-summary-badge quotation-summary-badge-won', label: 'Won' },
      'lost': { className: 'quotation-summary-badge quotation-summary-badge-lost', label: 'Lost' },
      'pending': { className: 'quotation-summary-badge quotation-summary-badge-pending', label: 'Pending' },
    };
    const config = statusConfig[status?.toLowerCase()] || statusConfig['pending'];
    return <span className={config.className}>{config.label}</span>;
  };

  const getPanelTitle = () => {
    if (selectedFilter === 'fromEnquiry') return 'Quotations From Enquiry';
    if (selectedFilter === 'direct') return 'Direct Quotations';
    return 'All Quotations';
  };

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  // Format amount
  const formatAmount = (amount: string) => {
    try {
      const numAmount = parseFloat(amount);
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
      }).format(numAmount);
    } catch {
      return amount;
    }
  };

  // Define columns for the table
  const columns = useMemo<CustomColumnDef<QuotationListingItem>[]>(
    () => {
      // Helper function to build dropdown URL with filters
      const buildDropdownUrl = (fieldName: string) => {
        const params = new URLSearchParams();
        if (dateFrom) {
          params.append('date_from', dateFrom.toISOString().split('T')[0]);
        }
        if (dateTo) {
          params.append('date_to', dateTo.toISOString().split('T')[0]);
        }
        if (warehouse.length > 0) {
          params.append('warehouse_id', warehouse.map(w => w.value).join(','));
        }
        params.append('filter_type', filterType);
        const queryString = params.toString();
        return `${api?.dashboad?.quotation_dropdown}/${fieldName}${queryString ? `?${queryString}` : ''}`;
      };

      const baseColumns: CustomColumnDef<QuotationListingItem>[] = [
        {
          header: "Quotation No.",
          accessorKey: "quotation_number",
          enableColumnFilter: true,
          enableSorting: true,
          filterType: "dropdown" as const,
          filterFetchUrl: buildDropdownUrl("quotation_number"),
          cell: ({ row }: { row: any }) => {
            return (
              <span className="quotation-summary-table-quotation-number">
                {row.original.quotation_number || '-'}
              </span>
            );
          },
        },
        {
          header: "Quotation Date",
          accessorKey: "quotation_date",
          enableColumnFilter: true,
          enableSorting: true,
          filterType: "date" as const,
          cell: ({ row }: { row: any }) => {
            return (
              <span className="quotation-summary-table-date">
                {formatDate(row.original.quotation_date)}
              </span>
            );
          },
        },
        {
          header: "Customer Name",
          accessorKey: "customer_name",
          enableColumnFilter: true,
          enableSorting: true,
          filterType: "dropdown" as const,
          filterFetchUrl: buildDropdownUrl("customer_name"),
          cell: ({ row }: { row: any }) => {
            return (
              <span className="quotation-summary-table-customer">
                {row.original.customer_name || '-'}
              </span>
            );
          },
        },
        {
          header: "Contact Person",
          accessorKey: "contact_person_name",
          enableColumnFilter: true,
          enableSorting: true,
          filterType: "dropdown" as const,
          filterFetchUrl: buildDropdownUrl("contact_person_name"),
          cell: ({ row }: { row: any }) => {
            return (
              <span className="quotation-summary-table-customer">
                {row.original.contact_person_name || '-'}
              </span>
            );
          },
        },
        {
          header: "Phone",
          accessorKey: "contact_person_phone_no",
          enableColumnFilter: true,
          enableSorting: false,
          filterType: "dropdown" as const,
          filterFetchUrl: buildDropdownUrl("contact_person_phone_no"),
          cell: ({ row }: { row: any }) => {
            return (
              <span className="quotation-summary-table-phone">
                {row.original.contact_person_phone_no || '-'}
              </span>
            );
          },
        },
        {
          header: "Amount",
          accessorKey: "total_amount",
          enableColumnFilter: false,
          enableSorting: true,
          cell: ({ row }: { row: any }) => {
            return (
              <span className="quotation-summary-table-amount">
                {formatAmount(row.original.total_amount)}
              </span>
            );
          },
        },
        {
          header: "Status",
          accessorKey: "stage",
          enableColumnFilter: true,
          enableSorting: true,
          filterType: "dropdown" as const,
          filterFetchUrl: buildDropdownUrl("stage"),
          cell: ({ row }: { row: any }) => {
            return getQuotationStatusBadge(row.original.stage);
          },
        },
        {
          header: "Source",
          accessorKey: "source",
          enableColumnFilter: false,
          enableSorting: false,
          cell: ({ row }: { row: any }) => {
            return (
              <span className={`quotation-summary-badge ${row.original.source === 'from_enquiry' ? 'quotation-summary-badge-source-enquiry' : 'quotation-summary-badge-source-direct'}`}>
                {row.original.source === 'from_enquiry' ? 'From Enquiry' : 'Direct'}
              </span>
            );
          },
        },
      ];

        // Only show Enquiry Ref column when not viewing direct quotations
      if (selectedFilter !== 'direct') {
        baseColumns.push({
          header: "Enquiry Ref",
          accessorKey: "enquiry_number",
          enableColumnFilter: true,
          enableSorting: false,
          filterType: "dropdown" as const,
          filterFetchUrl: buildDropdownUrl("enquiry_number"),
          cell: ({ row }: { row: any }) => {
            return (
              <span className="quotation-summary-table-enquiry-ref">
                {row.original.enquiry_number || '-'}
              </span>
            );
          },
        });
      }

      return baseColumns;
    },
    [selectedFilter, dateFrom, dateTo, warehouse, filterType]
  );

  return (
    <Card className="quotation-summary-main-card shadow-sm border-0 w-100 h-100">
      <Card.Body className="p-4">
        {/* Header */}
        <div className="mb-3 pb-2 border-bottom position-relative">
          <div className="d-flex justify-content-between align-items-center">
            <div className="order-summary-title-container">
              <FileText className="order-summary-icon" />
              <h3 className="order-summary-title">Quotation Analytics</h3>
            </div>
            <div className="quotation-summary-win-rate">
              <TrendingUp className="quotation-summary-win-rate-icon" />
              <span className="quotation-summary-win-rate-text">{conversionRate}% Win Rate</span>
            </div>
          </div>
        </div>

      {/* Clickable Summary Cards */}
      <div className="quotation-summary-cards-grid">
        <div 
          onClick={() => !loading && handleCardClick('all')}
          className={`quotation-summary-card quotation-summary-card-blue ${loading ? 'quotation-summary-card-loading' : ''}`}
        >
          <div className="quotation-summary-card-arrow">
            <ArrowRight className="quotation-summary-icon" style={{ color: '#2563eb' }} />
          </div>
          <p className="quotation-summary-card-label quotation-summary-card-label-blue">Total Quotations</p>
          {loading ? (
            <div className="d-flex align-items-center justify-content-center quotation-summary-card-value" style={{ minHeight: '2.5rem' }}>
              <Spinner animation="border" variant="primary" size="sm" />
            </div>
          ) : (
            <p className="quotation-summary-card-value">{totalQuotations}</p>
          )}
          
          {/* Status Breakdown */}
          <div className="quotation-summary-card-status-breakdown quotation-summary-card-status-breakdown-blue">
            <div className="quotation-summary-card-status-item">
              <p className="quotation-summary-card-status-label">Won</p>
              <p className="quotation-summary-card-status-value quotation-summary-card-status-value-won">{loading ? '-' : won}</p>
            </div>
            <div className="quotation-summary-card-status-item">
              <p className="quotation-summary-card-status-label">Lost</p>
              <p className="quotation-summary-card-status-value quotation-summary-card-status-value-lost">{loading ? '-' : lost}</p>
            </div>
            <div className="quotation-summary-card-status-item">
              <p className="quotation-summary-card-status-label">Pending</p>
              <p className="quotation-summary-card-status-value quotation-summary-card-status-value-pending">{loading ? '-' : pending}</p>
            </div>
          </div>
          
          <div className="quotation-summary-card-hint quotation-summary-card-hint-blue">
            <MousePointerClick className="quotation-summary-card-hint-icon" />
            <span>Click to view all</span>
          </div>
        </div>

        <div 
          onClick={() => !loading && handleCardClick('fromEnquiry')}
          className={`quotation-summary-card quotation-summary-card-purple ${loading ? 'quotation-summary-card-loading' : ''}`}
        >
          <div className="quotation-summary-card-arrow">
            <ArrowRight className="quotation-summary-icon" style={{ color: '#9333ea' }} />
          </div>
          <p className="quotation-summary-card-label quotation-summary-card-label-purple">From Enquiry</p>
          {loading ? (
            <div className="d-flex align-items-center justify-content-center quotation-summary-card-value" style={{ minHeight: '2.5rem' }}>
              <Spinner animation="border" variant="primary" size="sm" />
            </div>
          ) : (
            <p className="quotation-summary-card-value">{fromEnquiry}</p>
          )}
          
          {/* Status Breakdown */}
          <div className="quotation-summary-card-status-breakdown quotation-summary-card-status-breakdown-purple">
            <div className="quotation-summary-card-status-item">
              <p className="quotation-summary-card-status-label">Won</p>
              <p className="quotation-summary-card-status-value quotation-summary-card-status-value-won">{loading ? '-' : fromEnquiryWon}</p>
            </div>
            <div className="quotation-summary-card-status-item">
              <p className="quotation-summary-card-status-label">Lost</p>
              <p className="quotation-summary-card-status-value quotation-summary-card-status-value-lost">{loading ? '-' : fromEnquiryLost}</p>
            </div>
            <div className="quotation-summary-card-status-item">
              <p className="quotation-summary-card-status-label">Pending</p>
              <p className="quotation-summary-card-status-value quotation-summary-card-status-value-pending">{loading ? '-' : fromEnquiryPending}</p>
            </div>
          </div>
          
          <div className="quotation-summary-card-hint quotation-summary-card-hint-purple">
            <MousePointerClick className="quotation-summary-card-hint-icon" />
            <span>Click to view all</span>
          </div>
        </div>

        <div 
          onClick={() => !loading && handleCardClick('direct')}
          className={`quotation-summary-card quotation-summary-card-indigo ${loading ? 'quotation-summary-card-loading' : ''}`}
        >
          <div className="quotation-summary-card-arrow">
            <ArrowRight className="quotation-summary-icon" style={{ color: '#4f46e5' }} />
          </div>
          <p className="quotation-summary-card-label quotation-summary-card-label-indigo">Direct Quotations</p>
          {loading ? (
            <div className="d-flex align-items-center justify-content-center quotation-summary-card-value" style={{ minHeight: '2.5rem' }}>
              <Spinner animation="border" variant="primary" size="sm" />
            </div>
          ) : (
            <p className="quotation-summary-card-value">{direct}</p>
          )}
          
          {/* Status Breakdown */}
          <div className="quotation-summary-card-status-breakdown quotation-summary-card-status-breakdown-indigo">
            <div className="quotation-summary-card-status-item">
              <p className="quotation-summary-card-status-label">Won</p>
              <p className="quotation-summary-card-status-value quotation-summary-card-status-value-won">{loading ? '-' : directWon}</p>
            </div>
            <div className="quotation-summary-card-status-item">
              <p className="quotation-summary-card-status-label">Lost</p>
              <p className="quotation-summary-card-status-value quotation-summary-card-status-value-lost">{loading ? '-' : directLost}</p>
            </div>
            <div className="quotation-summary-card-status-item">
              <p className="quotation-summary-card-status-label">Pending</p>
              <p className="quotation-summary-card-status-value quotation-summary-card-status-value-pending">{loading ? '-' : directPending}</p>
            </div>
          </div>
          
          <div className="quotation-summary-card-hint quotation-summary-card-hint-indigo">
            <MousePointerClick className="quotation-summary-card-hint-icon" />
            <span>Click to view all</span>
          </div>
        </div>
      </div>

      {/* Slide-out Panel */}
      <DashboardSlidePanel
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        icon={FileText}
        title={getPanelTitle()}
        subtitle={`${totalRecords} quotations found`}
        loading={listLoading}
        columns={columns}
        data={quotationList}
        totalRecords={totalRecords}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPageChange={setPageIndex}
        onPageSizeChange={setPageSize}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        colValue={filterValue}
        setFilterValue={setFilterValue}
        sorting={sorting}
        onSortingChange={setSorting}
        searchPlaceholder="Search quotations..."
      />
      </Card.Body>
    </Card>
  );
}
